/**
 * Stat Card Component
 * Displays a statistic with icon, label, and value
 * Follows Single Responsibility Principle
 */

import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
  strokeColors,
} from '@/design-system/utils';
import { useUnifiedIcon } from '@/hooks/useUnifiedIcon';
import { IconWithBackground } from '../travel-guide/IconWithBackground';

export interface StatCardProps {
  title: string;
  value: string | null;
  icon?: string | React.ReactNode;
  className?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  icon: providedIcon,
  className,
}) => {
  const [needsTooltip, setNeedsTooltip] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [needsTitleTooltip, setNeedsTitleTooltip] = useState(false);
  const [showTitleTooltip, setShowTitleTooltip] = useState(false);
  const valueRef = useRef<HTMLParagraphElement>(null);
  const titleRef = useRef<HTMLParagraphElement>(null);

  // Use unified icon hook if icon is a string, otherwise use provided React node
  const { icon: unifiedIcon } = useUnifiedIcon({
    label: title,
    icon: typeof providedIcon === 'string' ? providedIcon : null,
    iconSize: 20,
  });

  // Use provided icon if it's a React node, otherwise use unified icon
  const iconToDisplay =
    typeof providedIcon !== 'string' && providedIcon
      ? providedIcon
      : unifiedIcon;

  // Check if value text is truncated
  useEffect(() => {
    const checkTruncation = () => {
      if (valueRef.current) {
        const isTruncated =
          valueRef.current.scrollWidth > valueRef.current.clientWidth;
        setNeedsTooltip(isTruncated);
      }
    };

    // Check after a delay to ensure layout is complete
    const timeoutId = setTimeout(checkTruncation, 300);
    window.addEventListener('resize', checkTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTruncation);
    };
  }, [value]);

  // Check if title text is truncated
  useEffect(() => {
    const checkTitleTruncation = () => {
      if (titleRef.current) {
        const isTruncated =
          titleRef.current.scrollWidth > titleRef.current.clientWidth;
        setNeedsTitleTooltip(isTruncated);
      }
    };

    // Check after a delay to ensure layout is complete
    const timeoutId = setTimeout(checkTitleTruncation, 300);
    window.addEventListener('resize', checkTitleTruncation);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', checkTitleTruncation);
    };
  }, [title]);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{
        duration: 0.4,
        ease: [0.4, 0, 0.2, 1],
        opacity: { duration: 0.3 },
      }}
      whileHover={{
        scale: 1.02,
        transition: {
          duration: 0.2,
          ease: [0.4, 0, 0.2, 1],
        },
      }}
      className={className}
      style={{
        display: 'flex',
        padding: `${spacing[2]}`, // 16px 40px 16px 12px
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: spacing[2.5], // 10px gap between header and value
        borderRadius: borders.radius.xl, // 12px
        border: `${borders.width.thin} solid ${strokeColors.light}`, // 1px solid #E7E5E4
        backgroundColor: backgroundColors.primary, // #FFF
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: spacing[2], // 8px gap between icon and label
          alignItems: 'center',
          width: '100%',
          minWidth: 0,
          position: 'relative',
        }}
        onMouseEnter={() => {
          if (needsTitleTooltip) {
            setShowTitleTooltip(true);
          }
        }}
        onMouseLeave={() => setShowTitleTooltip(false)}
        onTouchStart={() => {
          if (needsTitleTooltip) {
            setShowTitleTooltip(true);
            // Auto-hide after 3 seconds on touch devices
            setTimeout(() => {
              setShowTitleTooltip(false);
            }, 3000);
          }
        }}
      >
        {iconToDisplay && (
          <IconWithBackground icon={iconToDisplay} iconSize={20} alt={title} />
        )}
        <p
          ref={titleRef}
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.sm,
            fontWeight: typography.weight.regular,
            color: typographyColors.secondary,
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            minWidth: 0,
          }}
        >
          {title}
        </p>
        {/* Title Tooltip */}
        {showTitleTooltip && needsTitleTooltip && title && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: spacing[2],
              padding: `${spacing[2]} ${spacing[3]}`,
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              color: '#ffffff',
              borderRadius: borders.radius.md,
              fontSize: typography.size.sm,
              fontFamily: fontPrimary,
              whiteSpace: 'nowrap',
              zIndex: 1000,
              pointerEvents: 'none',
              maxWidth: '400px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            {title}
            {/* Tooltip arrow */}
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `6px solid rgba(0, 0, 0, 0.85)`,
              }}
            />
          </div>
        )}
      </div>
      <div
        style={{
          position: 'relative',
          width: '100%',
        }}
        onMouseEnter={() => {
          if (needsTooltip) {
            setShowTooltip(true);
          }
        }}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <p
          ref={valueRef}
          style={{
            fontFamily: fontPrimary,
            fontSize: typography.size.xl,
            fontWeight: typography.weight.semibold,
            color: typographyColors.primary,
            margin: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {value || 'No Data Available'}
        </p>
        {/* Tooltip */}
        {showTooltip && needsTooltip && value && (
          <div
            style={{
              position: 'absolute',
              bottom: '100%',
              left: '50%',
              transform: 'translateX(-50%)',
              marginBottom: spacing[2],
              padding: `${spacing[2]} ${spacing[3]}`,
              backgroundColor: 'rgba(0, 0, 0, 0.85)',
              color: '#ffffff',
              borderRadius: borders.radius.md,
              fontSize: typography.size.sm,
              fontFamily: fontPrimary,
              whiteSpace: 'nowrap',
              zIndex: 1000,
              pointerEvents: 'none',
              maxWidth: '400px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
            }}
          >
            {value}
            {/* Tooltip arrow */}
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `6px solid rgba(0, 0, 0, 0.85)`,
              }}
            />
          </div>
        )}
      </div>
    </motion.div>
  );
};
